/**
 * GameTabs component for the game scoring interface.
 *
 * Three tabs:
 * 1. First half (1. pääty)
 * 2. Second half (2. pääty)
 * 3. Results (Tulos)
 */

import { useState, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Button,
  Alert,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { HalfScoreForm } from '@/features/judge/components/HalfScoreForm';
import { ResultsPanel } from '@/features/judge/components/ResultsPanel';
import type { LocalGameState, HalfScore } from '@/types';
import { getHalfDisplayScore } from '@/types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      sx={{ py: 2 }}
    >
      {value === index && children}
    </Box>
  );
}

interface GameTabsProps {
  game: LocalGameState;
  onSubmitFirstHalf: (team1: HalfScore, team2: HalfScore) => void;
  onSubmitSecondHalf: (team1: HalfScore, team2: HalfScore) => void;
  onSubmitOvertime: (team1: HalfScore, team2: HalfScore) => void;
  onEditHalf: (half: 'firstHalf' | 'secondHalf') => void;
  onEndGame: () => void;
  onCancelGame: () => void;
  isSubmitting?: boolean;
}

export function GameTabs({
  game,
  onSubmitFirstHalf,
  onSubmitSecondHalf,
  onSubmitOvertime,
  onEditHalf,
  onEndGame,
  onCancelGame,
  isSubmitting = false,
}: GameTabsProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [team1Score, setTeam1Score] = useState<HalfScore>({ akka: 0, pappi: 0 });
  const [team2Score, setTeam2Score] = useState<HalfScore>({ akka: 0, pappi: 0 });
  const [showOvertimeForm, setShowOvertimeForm] = useState(false);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleScoreChange = useCallback((t1: HalfScore, t2: HalfScore) => {
    setTeam1Score(t1);
    setTeam2Score(t2);
  }, []);

  const handleSubmitFirstHalf = () => {
    onSubmitFirstHalf(team1Score, team2Score);
    setActiveTab(1); // Move to second half
    setTeam1Score({ akka: 0, pappi: 0 });
    setTeam2Score({ akka: 0, pappi: 0 });
  };

  const handleSubmitSecondHalf = () => {
    onSubmitSecondHalf(team1Score, team2Score);
    setActiveTab(2); // Move to results
  };

  const handleEditFirstHalf = () => {
    onEditHalf('firstHalf');
    setTeam1Score(game.firstHalf.team1);
    setTeam2Score(game.firstHalf.team2);
  };

  const handleEditSecondHalf = () => {
    onEditHalf('secondHalf');
    setTeam1Score(game.secondHalf.team1);
    setTeam2Score(game.secondHalf.team2);
  };

  const handleShowOvertimeForm = () => {
    setShowOvertimeForm(true);
    setTeam1Score({ akka: 0, pappi: 0 });
    setTeam2Score({ akka: 0, pappi: 0 });
  };

  const handleSubmitOvertime = () => {
    onSubmitOvertime(team1Score, team2Score);
    setShowOvertimeForm(false);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Peli: {game.id}
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        {game.team1.name} vs {game.team2.name}
      </Typography>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          label="1. pääty"
          sx={{ fontWeight: game.firstHalf.ready ? 600 : 400 }}
        />
        <Tab
          label="2. pääty"
          disabled={!game.firstHalf.ready}
          sx={{ fontWeight: game.secondHalf.ready ? 600 : 400 }}
        />
        <Tab label="Tulos" />
      </Tabs>

      {/* First Half Tab */}
      <TabPanel value={activeTab} index={0}>
        {game.firstHalf.ready ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              1. pääty valmis!
            </Alert>

            {/* Read-only score display */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography fontWeight={600}>{game.team1.name}</Typography>
                <Typography variant="h5" fontWeight={700}>
                  {getHalfDisplayScore(game.firstHalf.team1)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {game.firstHalf.team1.akka} akkaa, {game.firstHalf.team1.pappi} pappia
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography fontWeight={600}>{game.team2.name}</Typography>
                <Typography variant="h5" fontWeight={700}>
                  {getHalfDisplayScore(game.firstHalf.team2)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {game.firstHalf.team2.akka} akkaa, {game.firstHalf.team2.pappi} pappia
              </Typography>
            </Paper>

            <Button
              variant="outlined"
              color="warning"
              fullWidth
              size="large"
              onClick={handleEditFirstHalf}
              sx={{ py: 1.5 }}
            >
              Muokkaa päätyä
            </Button>
          </Box>
        ) : (
          <Box>
            <HalfScoreForm
              team1Name={game.team1.name}
              team2Name={game.team2.name}
              initialTeam1={game.firstHalf.team1}
              initialTeam2={game.firstHalf.team2}
              onScoreChange={handleScoreChange}
            />
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={handleSubmitFirstHalf}
              disabled={isSubmitting}
              sx={{ mt: 2, py: 2 }}
            >
              1. pääty valmis!
            </Button>

            <Divider sx={{ my: 3 }} />

            <Button
              variant="outlined"
              color="error"
              size="large"
              fullWidth
              startIcon={<CancelIcon />}
              onClick={onCancelGame}
              disabled={isSubmitting}
              sx={{ py: 1.5 }}
            >
              Nollaa peli
            </Button>
          </Box>
        )}
      </TabPanel>

      {/* Second Half Tab */}
      <TabPanel value={activeTab} index={1}>
        {!game.firstHalf.ready ? (
          <Alert severity="warning">
            1. pääty ei ole vielä valmis. Pääset muokkaamaan tämän päädyn tietoja
            vasta, kun aiempi pääty on valmis.
          </Alert>
        ) : game.secondHalf.ready ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              2. pääty valmis!
            </Alert>

            {/* Read-only score display */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography fontWeight={600}>{game.team1.name}</Typography>
                <Typography variant="h5" fontWeight={700}>
                  {getHalfDisplayScore(game.secondHalf.team1)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {game.secondHalf.team1.akka} akkaa, {game.secondHalf.team1.pappi} pappia
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography fontWeight={600}>{game.team2.name}</Typography>
                <Typography variant="h5" fontWeight={700}>
                  {getHalfDisplayScore(game.secondHalf.team2)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {game.secondHalf.team2.akka} akkaa, {game.secondHalf.team2.pappi} pappia
              </Typography>
            </Paper>

            <Button
              variant="outlined"
              color="warning"
              fullWidth
              size="large"
              onClick={handleEditSecondHalf}
              sx={{ py: 1.5 }}
            >
              Muokkaa päätyä
            </Button>
          </Box>
        ) : (
          <Box>
            <HalfScoreForm
              team1Name={game.team1.name}
              team2Name={game.team2.name}
              initialTeam1={game.secondHalf.team1}
              initialTeam2={game.secondHalf.team2}
              onScoreChange={handleScoreChange}
            />
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={handleSubmitSecondHalf}
              disabled={isSubmitting}
              sx={{ mt: 2, py: 2 }}
            >
              2. pääty valmis!
            </Button>
          </Box>
        )}
      </TabPanel>

      {/* Results Tab */}
      <TabPanel value={activeTab} index={2}>
        {showOvertimeForm ? (
          <Box>
            <Typography variant="h6" gutterBottom>
              Jatkoaika
            </Typography>
            <HalfScoreForm
              team1Name={game.team1.name}
              team2Name={game.team2.name}
              onScoreChange={handleScoreChange}
            />
            <Button
              variant="contained"
              color="warning"
              size="large"
              fullWidth
              onClick={handleSubmitOvertime}
              disabled={isSubmitting}
              sx={{ mt: 2, py: 2 }}
            >
              Lähetä jatkoajan tulokset
            </Button>
          </Box>
        ) : (
          <ResultsPanel
            game={game}
            onSubmitScore={onEndGame}
            onSubmitOvertime={handleShowOvertimeForm}
            onCancelGame={onCancelGame}
            isSubmitting={isSubmitting}
          />
        )}
      </TabPanel>
    </Box>
  );
}

export default GameTabs;

